import React from 'react';
import { isAxiosError } from 'axios';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type LoaderFunctionArgs,
} from 'react-router';
import { Box, CircularProgress, Skeleton } from '@mui/material';
import { useSelector } from 'react-redux';
import {
  getPropertyWithDetails,
  getUserPropertyWithDetails,
} from '@/api/istaff/property';
import {
  selectIsAuthenticated,
  selectIsAuthResolved,
  selectUser,
} from '@/slices/auth';
import { store } from '@/state';
import { resolveSidebarRole } from '@/components/Sidebar';

const RouteFallback = () => (
  <Box sx={{ p: 3 }}>
    <Skeleton variant="rounded" height={160} />
  </Box>
);

const ProtectedRoute = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const isAuthResolved = useSelector(selectIsAuthResolved);

  if (!isAuthResolved) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
};

const router = createBrowserRouter([
  {
    path: 'login',
    HydrateFallback: RouteFallback,
    async lazy() {
      const { default: Login } = await import('./Login');
      return { Component: Login };
    },
  },
  {
    path: 'register',
    HydrateFallback: RouteFallback,
    async lazy() {
      const { default: Register } = await import('./Register');
      return { Component: Register };
    },
  },
  {
    path: 'forgot-password',
    HydrateFallback: RouteFallback,
    async lazy() {
      const { default: ForgotPassword } = await import('./ForgotPassword');
      return { Component: ForgotPassword };
    },
  },
  {
    path: 'terms',
    HydrateFallback: RouteFallback,
    async lazy() {
      const { default: TermsAndConditions } = await import(
        './TermsAndConditions'
      );
      return { Component: TermsAndConditions };
    },
  },
  {
    path: 'reset-password/:token',
    HydrateFallback: RouteFallback,
    async lazy() {
      const { default: ResetPassword } = await import('./ResetPassword');
      return { Component: ResetPassword };
    },
  },
  {
    path: 'reset-password',
    HydrateFallback: RouteFallback,
    async lazy() {
      const { default: ResetPassword } = await import('./ResetPassword');
      return { Component: ResetPassword };
    },
  },
  {
    path: 'verify-email/:id/:hash',
    HydrateFallback: RouteFallback,
    async lazy() {
      const { default: VerifyEmail } = await import('./VerifyEmail');
      return { Component: VerifyEmail };
    },
  },
  {
    path: 'auth/social/callback',
    HydrateFallback: RouteFallback,
    async lazy() {
      const { default: SocialCallback } = await import(
        './SocialCallback/SocialCallback'
      );
      return { Component: SocialCallback };
    },
  },
  {
    Component: ProtectedRoute,
    HydrateFallback: RouteFallback,
    children: [
      {
        HydrateFallback: RouteFallback,
        async lazy() {
          const { default: RootLayout } = await import('@/layouts/RootLayout');
          return { Component: RootLayout };
        },
        children: [
          {
            index: true,
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: RoleHome } = await import('./RoleHome');
              return { Component: RoleHome };
            },
          },
          {
            path: 'properties',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: PropertiesPage } = await import(
                './AgencyProperties/AgencyProperties'
              );
              return { Component: PropertiesPage };
            },
          },
          {
            path: 'admin/properties',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: PropertiesPage } = await import(
                './AgencyProperties/AgencyProperties'
              );
              return { Component: PropertiesPage };
            },
          },
          {
            path: 'admin/my-properties',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: PropertiesPage } = await import(
                './AgencyProperties/AgencyProperties'
              );
              return { Component: () => <PropertiesPage ownerOnly /> };
            },
          },
          {
            path: 'admin/offer-requests',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminOfferRequests } = await import(
                './AdminOfferRequests'
              );
              return { Component: AdminOfferRequests };
            },
          },
          {
            path: 'admin/user-requests',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminUserRequests } = await import(
                './AdminUserRequests'
              );
              return { Component: AdminUserRequests };
            },
          },
          {
            path: 'admin/management',
            Component: () => <Navigate to="/admin/management/users" replace />,
          },
          {
            path: 'admin/management/users',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementUsers } = await import(
                './AdminManagementUsers'
              );
              return { Component: AdminManagementUsers };
            },
          },
          {
            path: 'admin/management/amenities',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementAmenities } = await import(
                './AdminManagementAmenities'
              );
              return { Component: AdminManagementAmenities };
            },
          },
          {
            path: 'admin/management/areas',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementAreas } = await import(
                './AdminManagementAreas'
              );
              return { Component: AdminManagementAreas };
            },
          },
          {
            path: 'admin/management/seasons',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementSeasons } = await import(
                './AdminManagementSeasons'
              );
              return { Component: AdminManagementSeasons };
            },
          },
          {
            path: 'my-requests',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: CompanyMyRequests } = await import(
                './CompanyMyRequests'
              );
              return { Component: CompanyMyRequests };
            },
          },
          {
            path: 'favorites',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: Favorites } = await import('./Favorites');
              return { Component: Favorites };
            },
          },
          {
            path: 'admin/submissions',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: PropertiesPage } = await import(
                './AgencyProperties/AgencyProperties'
              );
              return {
                Component: () => <PropertiesPage fixedStatusId={2} />,
              };
            },
          },
        ],
      },
      {
        HydrateFallback: RouteFallback,
        async lazy() {
          const { default: HeaderLayout } = await import(
            '@/layouts/HeaderLayout'
          );
          return { Component: HeaderLayout };
        },
        children: [
          {
            path: 'me',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: Me } = await import('./Me');
              return { Component: Me };
            },
          },
          {
            path: 'property/:id',
            HydrateFallback: RouteFallback,
            async loader({ params, request }: LoaderFunctionArgs) {
              const id = params.id!;
              const user = selectUser(store.getState());
              const role = resolveSidebarRole({
                roles: user?.roles,
                type: user?.type,
              });
              const usesManagementEndpoint =
                role === 'agent' || role === 'admin';
              const scope = new URL(request.url).searchParams.get('scope');
              const [primary, fallback] =
                usesManagementEndpoint || scope === 'own'
                  ? [getUserPropertyWithDetails, getPropertyWithDetails]
                  : [getPropertyWithDetails, getUserPropertyWithDetails];
              try {
                return await primary(id, { skipErrorToast: true });
              } catch (error) {
                if (isAxiosError(error) && error.response?.status === 404) {
                  return fallback(id);
                }
                throw error;
              }
            },
            async lazy() {
              const { default: Property } = await import('./Property');
              return { Component: Property };
            },
          },
          {
            path: 'my-requests/:id',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: CompanyRequestDetail } = await import(
                './CompanyRequestDetail'
              );
              return { Component: CompanyRequestDetail };
            },
          },
          {
            path: 'admin/offer-requests/:id',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminOfferRequestDetail } = await import(
                './AdminOfferRequestDetail'
              );
              return { Component: AdminOfferRequestDetail };
            },
          },
          {
            path: 'admin/user-requests/:id',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: CompanyRequestDetail } = await import(
                './CompanyRequestDetail'
              );
              return { Component: CompanyRequestDetail };
            },
          },
          {
            path: 'admin/management/users/new',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementUserCreate } = await import(
                './AdminManagementUserCreate'
              );
              return { Component: AdminManagementUserCreate };
            },
          },
          {
            path: 'admin/management/users/:id',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementUserDetail } = await import(
                './AdminManagementUserDetail'
              );
              return { Component: AdminManagementUserDetail };
            },
          },
          {
            path: 'admin/management/users/:id/edit',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementUserEdit } = await import(
                './AdminManagementUserEdit'
              );
              return { Component: AdminManagementUserEdit };
            },
          },
          {
            path: 'admin/management/amenities/new',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementAmenityForm } = await import(
                './AdminManagementAmenityForm'
              );
              return {
                Component: () => <AdminManagementAmenityForm mode="create" />,
              };
            },
          },
          {
            path: 'admin/management/seasons/new',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminSeasonForm } = await import(
                './AdminManagementSeasons/AdminSeasonForm'
              );
              return {
                Component: () => <AdminSeasonForm mode="create" />,
              };
            },
          },
          {
            path: 'admin/management/amenities/:id',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementAmenityDetail } = await import(
                './AdminManagementAmenityDetail'
              );
              return { Component: AdminManagementAmenityDetail };
            },
          },
          {
            path: 'admin/management/amenities/:id/edit',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementAmenityForm } = await import(
                './AdminManagementAmenityForm'
              );
              return {
                Component: () => <AdminManagementAmenityForm mode="edit" />,
              };
            },
          },
          {
            path: 'admin/management/seasons/:id',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminSeasonDetail } = await import(
                './AdminManagementSeasons/AdminSeasonDetail'
              );
              return { Component: AdminSeasonDetail };
            },
          },
          {
            path: 'admin/management/seasons/:id/edit',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminSeasonForm } = await import(
                './AdminManagementSeasons/AdminSeasonForm'
              );
              return {
                Component: () => <AdminSeasonForm mode="edit" />,
              };
            },
          },
          {
            path: 'admin/management/areas/:id',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementAreaDetail } = await import(
                './AdminManagementAreaDetail'
              );
              return { Component: AdminManagementAreaDetail };
            },
          },
          {
            path: 'admin/management/areas/:id/edit',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AdminManagementAreaForm } = await import(
                './AdminManagementAreaForm'
              );
              return { Component: AdminManagementAreaForm };
            },
          },
          {
            path: 'properties/new',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AddProperty } = await import(
                './AddProperty/AddProperty'
              );
              return { Component: AddProperty };
            },
          },
          {
            path: 'properties/:id/edit',
            HydrateFallback: RouteFallback,
            async lazy() {
              const { default: AddProperty } = await import(
                './AddProperty/AddProperty'
              );
              return { Component: AddProperty };
            },
          },
        ],
      },
    ],
  },
]);

export default router;
